import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Toaster } from 'react-hot-toast'
import { BrowserRouter } from 'react-router-dom'
import './index.css'
import App from './App'
import { DialogProvider } from './components/dialogs/DialogProvider'
import { ThemeProvider } from './hooks/useTheme'
import {
  currentOrgSlug,
  isBareAuthRoute,
  resolveOrgSlug,
  slugAuthRedirectTarget,
} from './utils/org-slug'
import { initSentry } from './utils/sentry'

// Initialize Sentry error monitoring if DSN is configured
initSentry()

// Auth pages live only at bare URLs: /<slug>/login (and register /
// forgot-password / reset-password) always redirect to the bare form, so
// slug-prefixed auth URLs are never allowed. Every other SPA URL must carry
// an `/<orgSlug>` prefix: bare non-auth paths (a bookmark from the
// single-tenant era) redirect once under the default org, then React Router's
// `basename` strips the prefix so all in-app absolute paths resolve org-scoped.
const slugAuthRedirect = slugAuthRedirectTarget()
if (slugAuthRedirect !== null) {
  window.location.replace(slugAuthRedirect)
} else if (resolveOrgSlug() === undefined && !isBareAuthRoute()) {
  const { pathname, search, hash } = window.location
  window.location.replace(`/${currentOrgSlug()}${pathname === '/' ? '' : pathname}${search}${hash}`)
}

const shouldRender =
  slugAuthRedirect === null &&
  (resolveOrgSlug() !== undefined || isBareAuthRoute(window.location.pathname))
const routerBasename = resolveOrgSlug() !== undefined ? `/${resolveOrgSlug()}` : '/'

if (shouldRender) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <BrowserRouter basename={routerBasename}>
        <ThemeProvider>
          <DialogProvider>
            <App />
          </DialogProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'var(--harbor-surface)',
                color: 'var(--harbor-text)',
                border: '1px solid var(--harbor-border)',
                borderRadius: '8px',
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: 'var(--harbor-surface)',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: 'var(--harbor-surface)',
                },
              },
            }}
          />
        </ThemeProvider>
      </BrowserRouter>
    </StrictMode>
  )
}
